import { useState, useEffect } from 'react';

// A deploy happens a handful of times a day, so a 5-minute cadence is ample. The poll is
// also gated on tab visibility — a hidden tab cannot act on the reload toast, so it has no
// reason to keep asking. Returning to the tab checks immediately, floored by MIN_GAP so
// rapid tab switching cannot outpace the interval.
const POLL_INTERVAL = 300_000;
const MIN_GAP = 60_000;

export const useUpdateChecker = () => {
  const [updateAvailable, setUpdateAvailable] = useState(false);

  useEffect(() => {
    const worker = new Worker(new URL('../workers/refresh-after-update.ts', import.meta.url), { type: 'module' });
    const handle = { interval: undefined as ReturnType<typeof setInterval> | undefined };
    let stopped = false;
    let lastChecked = 0;

    const check = () => {
      if (stopped || document.visibilityState !== 'visible') return;

      const now = Date.now();
      if (now - lastChecked < MIN_GAP) return;

      lastChecked = now;
      worker.postMessage('CHECK_UPDATES');
    };

    worker.onmessage = (event) => {
      if (event.data.type !== 'UPDATE_AVAILABLE') return;

      setUpdateAvailable(true);
      // The toast is persistent, so one detection is all we need — leaving the interval
      // running would poll forever behind a notice the user has already seen.
      stopped = true;
      clearInterval(handle.interval);
    };

    handle.interval = setInterval(check, POLL_INTERVAL);
    document.addEventListener('visibilitychange', check);

    return () => {
      stopped = true;
      clearInterval(handle.interval);
      document.removeEventListener('visibilitychange', check);
      worker.terminate();
    };
  }, []);

  return updateAvailable;
};
